import { memo } from 'react'
import type { JSX } from 'react'

import { Button, SquarePenIcon, Text } from '@buffer-mono/popcorn'

import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { usePrivateAccountPopover } from './hooks/usePrivateAccountPopover'
import { useCommentData } from '../context/CommentContext'
import { isPrivateLinkedInAccount } from '../../util/privateAccount'
import { CommentHoverActions } from '../../CommentDetails/CommentHoverActions'
import { EngagementHeader } from '../../components/EngagementHeader'

export const CommentHeader_Channel = graphql(/* GraphQL */ `
  fragment CommentHeader_Channel on Channel @_unmask {
    id
  }
`)

type CommentHeaderProps = {
  /**
   * Id for the header element, referenced by the row's `aria-labelledby`.
   * Defaults to `comment-header-${comment.id}`.
   */
  id?: string
  /** whether this is a context comment */
  isContextComment?: boolean
  /** whether the comment item is being hovered */
  isHovered?: boolean
  /** whether this is the user's own reply */
  isOwnReply?: boolean
  /** channel for getting configuration */
  channel?: FragmentOf<typeof CommentHeader_Channel>
}

/**
 * Returns the display name for a comment author.
 * LinkedIn returns a cryptic username (member URN), so we use display name instead.
 */
const getDisplayName = (comment: ReturnType<typeof useCommentData>): string => {
  if (comment.serviceType === 'linkedin' || comment.serviceType === 'tiktok') {
    return comment.author.name || comment.author.username
  }
  return comment.author.username
}

const Username = (): JSX.Element => {
  const comment = useCommentData()

  return (
    <Text weight="bold" translate="no">
      {getDisplayName(comment)}
    </Text>
  )
}

type CreatePostButtonProps = {
  channelId: string
}

const CreatePostButton = memo(
  ({ channelId }: CreatePostButtonProps): JSX.Element => {
    const comment = useCommentData()

    return (
      <NewPostComposerTrigger
        cta="publish-comments-commentHeader-newPost-1"
        channels={[channelId]}
        prefillPostData={{
          text: comment.text,
          media:
            comment.assets.length > 0
              ? {
                  thumbnail: comment.assets[0].thumbnail,
                  picture: comment.assets[0].source,
                  // TODO: Review if we should use the actual width and height of the image
                  // This is currently hardcoded because the types are mandatory and we don't have a way to get the actual width and height of the image
                  width: '',
                  height: '',
                  progress: '100',
                  uploaded: 'true',
                }
              : undefined,
        }}
      >
        <Button
          variant="secondary"
          size="small"
          aria-label="Create a post from this reply"
        >
          <SquarePenIcon /> Create a Post
        </Button>
      </NewPostComposerTrigger>
    )
  },
)
CreatePostButton.displayName = 'CreatePostButton'

export const CommentHeader = ({
  id,
  isContextComment = false,
  isHovered = false,
  isOwnReply = false,
  channel,
}: CommentHeaderProps): JSX.Element => {
  const comment = useCommentData()

  const channelId = channel?.id

  const isPrivateAccount = isPrivateLinkedInAccount({
    serviceType: comment.serviceType,
    comment,
  })
  const { PrivateAccountPopover } = usePrivateAccountPopover()

  const showStatusBadge =
    isContextComment &&
    (comment.status === 'replied' || comment.status === 'dismissed')
  const statusBadgeText = comment.status === 'replied' ? 'Replied' : 'Resolved'
  const showDraftPostButton = isHovered && isOwnReply && channelId

  const username = isPrivateAccount ? (
    <PrivateAccountPopover>
      <Username />
    </PrivateAccountPopover>
  ) : (
    <Username />
  )

  return (
    <EngagementHeader
      id={id ?? `comment-header-${comment.id}`}
      username={username}
      timestamp={comment.publishedAt}
      statusLabel={showStatusBadge ? statusBadgeText : undefined}
      actions={
        showDraftPostButton ? (
          <CreatePostButton channelId={channelId} />
        ) : undefined
      }
      trailing={<CommentHoverActions />}
    />
  )
}
